import React,{useState,useEffect} from "react";
import { Box, Checkbox, IconButton, Tooltip, Typography } from "@mui/material";
import { Select, MenuItem } from "@mui/material";
import { startCase } from "lodash";
import FilterListIcon from '@mui/icons-material/FilterList';
import * as d3 from "d3";
import * as XLSX from 'xlsx-js-style';
import { getCurrentTerms } from "./projectTypeUtils";
import DeleteIcon from '@mui/icons-material/Delete';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { gridPageCountSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  formatTimestampForDisplay, 
  convertFromTimestamp as convertFromTimestampUtil,
  formatUnixDate as formatUnixDateUtil 
} from '../utils/timezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const TERMS = getCurrentTerms()
const DECIMAL_PRECISION = 0;

export const roundDown = (num) => {
  const factor = Math.pow(10, DECIMAL_PRECISION);
  return Math.floor(num * factor) / factor;
};

export function getRandomHexColor() {
  const hex = roundDown(Math.random() * 16777215).toString(16);
  return `#${hex.padStart(6, '0')}`;
}


export const activityList = (data) => {
  const parentMap = {};
  const result = [];

  Object.values(data).forEach((item) => {
    if (item.parent_activity_id === null) {
      parentMap[item.id] = {
        label: item?.name,
        value: item?.name,
        weight: item?.weight,
        id: item?.id,
        part_id: item?.part_id,
        children: [],
      };
      result.push(parentMap[item.id]);
    } else {
      const parent = parentMap[item.parent_activity_id];
      if (parent) {
        parent.children.push({
          label: item.name,
          value: item.name,
          id: item?.id,
          weight: item?.weight,
          part_id: item?.part_id
        });
      }
    }
  });

  return result;
};


export const addProgressToActivities = (activities, progressData) => {
  return activities.map(activity => {
    const progressMatch = progressData.find(p => p.activity_id === activity.id);

    const updatedActivity = {
      ...activity,
      progress: progressMatch ? progressMatch.progress : null
    };

    if (updatedActivity.children && updatedActivity.children.length > 0) {
      updatedActivity.children = updatedActivity.children.map((child) => {
        const childProgressMatch = progressData.find(p => p.activity_id === child.id);
        return {
          ...child,
          progress: childProgressMatch ? childProgressMatch.progress : null
        };
      });
    }

    return updatedActivity;
  });
};


export const getChildrenOnly = (data) => {
  return data.flatMap((parent) => {
    return parent.children && parent.children.length > 0 ? parent.children : [];
  });
};

export const calculateWeightedAverageProgress = (data) => {
  const totalWeightedProgress = data.reduce((acc, item) => acc + item.weight * item.progress, 0);
  const totalWeight = data.reduce((acc, item) => acc + item.weight, 0);

  return totalWeight > 0 ? (totalWeightedProgress / totalWeight) : 0;
};


export const groupByTrackerPrefix = (data) => {
  const grouped = {};

  data?.forEach(item => {
    const prefix = item.tracker_type.split('_').slice(0, 2).join('_');

    if (!grouped[prefix]) {
      grouped[prefix] = {
        tracker_type: prefix,
        count: 0,
        children: []
      };
    }

    grouped[prefix].children.push(item);

    grouped[prefix].count += item.count;
  });

  return Object.values(grouped);
};


export const getPartName = (partId, partsArray) => {
  if (!partsArray || !Array.isArray(partsArray) || !partId) {
    return null;
  }
  
  const part = partsArray.find(part => part.id === partId);
  return part ? part.part_name : null;
};

export const addConstructionName = (firstArr, constructionObj) => {
  return firstArr.map(item => {
    const distributionMatch = constructionObj?.[item?.distribution];
    const installationMatch = constructionObj?.[item?.installed];

    let updatedItem = { ...item };

    if (distributionMatch) {
      updatedItem.distribution_name = distributionMatch.name;
    }

    if (installationMatch) {
      updatedItem.installed_name = installationMatch.name;
    }

    return updatedItem;
  });
};


export const formatTimestamp = (ts) => {
  return formatTimestampForDisplay(ts);
};

export const prepareColumnsAndGroups = (colValue, handleOpenFilter, filters = [], handleImageClick, handleDelete, selectPart, filterableColumns = [], sort= []) => {
  
  if (!Array.isArray(filters)) {
    filters = [];
  }
  const columnGroupingModel = [];

  const getColor = (value) => {
    if (value < 1) {
      return ""
    } else if (value === 100) {
      return "#06b706"
    } else {
      return "#fc8eac"
    }
  }

  const getLabel = (value) => {
    if (value === "block") return "block"
    if (value === "time_stamp") return "time"
    if (value === "status") return "status"
    if (value === "checkpoint") return "checkpoint_name"
    if (value === "punchlist_category") return "punchlist_category"
    if (value === "itr_reference_number") return "itr_reference_number"
    if (value === "section") return "section"
    if (value === "row") return "row"
    if (value === "scope") return "scope"
    if(value === "part_name") return "part_number"
    else return value
  }

  const getHeadserLabel = (value) => {
    if (value === "section") return TERMS.SECTION_HEADER
    if (value === "row") return TERMS.ROW_HEADER
    else return value
  }

  const visibleColumns = colValue
    .filter((col) => !col.hidden)
    .flatMap((col) => {
      const getWidth = (value) => {
        if (value === 'part_name' || value === "comments" || value === "punchlist_status") return 170;
        if (value === 'part_description') return 360;
        if (value === "checkpoint") return 190;
        if (value === "laydown") return 190;
        if (value === 'time_stamp' || value === 'acceptance_crieteria' || value === "photos") return 150;
        if (value === "punchlist_category") return 200;
        if (value === "itr_reference_number") return 220;
        if (value === "section") return 100;
        if (value === "row") return 80;
        if (value === "scope") return 100;
        if (value === 'distributed_quantity') return 120; 
        if (value === 'installed_quantity') return 100;  
        if (value === 'quantity_yet_to_receive') return 120;
        if (value.includes('current.') || value.includes('damaged.') || value.includes('remediated.')) return 140;
        return 100;
      };

      if (col.children?.length) {
        return col.children.map((child) => {
          const fieldName = `${col.value}.${child}`;
          const columnWidth = getWidth(fieldName);
          return {
            field: fieldName,
            headerName: startCase(child),
            headerClassName: `${col.value}-header`,
            sortable: false,
            filterable: true,
            width: columnWidth,
            minWidth: columnWidth,
            flex: 1,
            renderHeader: (params) => (
              <div style={{ fontWeight: 'bold', width: '100%', textAlign: 'center' }}>
                {params.colDef.headerName}
              </div>
            ),
            renderCell: (params) => {
              const value = params.row;
              if (child === 'qty') {
                return <div style={{ background: getColor(roundDown(value["distribution_progress"])) }}>{value[`${col.value}_${"quantity"}`]}</div>;
              } else if (child === 'percentage' && col.value === "distributed") {
                return <div style={{ background: getColor(roundDown(value["distribution_progress"])) }}>{roundDown(value["distribution_progress"])}%</div>;
              } else if (child === 'percentage') {
                return <div style={{ background: getColor(roundDown(value[`${col.value}_${"progress"}`])) }}>{roundDown(value[`${col.value}_${"progress"}`])}%</div>;
              } else if (col.value === 'current' || col.value === 'damaged' || col.value === 'remediated') {
                return <div style={{ textAlign: 'center' }}>{value[fieldName] || 0}</div>;
              }
              else {
                return <div style={{ background: getColor(roundDown(value[`${col.value}_${"progress"}`])) }}>{value[`${col.value}_${child.value}`]}</div>;
              }
            }
          };
        });
      } else {
        const columnWidth = getWidth(col.value);
        return {
          field: col.value === "part_description" ? "description" : col.value,
          headerName: (selectPart?.length && col.value === "project_quantity") ? "Required Quantity" : startCase(getHeadserLabel(col.value)),
          headerClassName: "block",
          sortable: sort.length ? sort.includes(col.value) : false,
          filterable: true,
          width: columnWidth,
          minWidth: columnWidth,
          disableColumnMenu: true,
          flex: 1,
          renderHeader: (params) => {

            if (filterableColumns.includes(col.value)) {
              const currentFilter = getLabel(col.value);
              const hasFilter = filters?.find((item) => Object.keys(item).includes(currentFilter));
              const filterLength = hasFilter ? hasFilter?.[currentFilter]?.length : 0;

              return (
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenFilter(event, currentFilter);
                  }}
                  key={params.colDef.headerName}
                  className="MuiDataGrid-columnHeader custom-header"
                  style={{
                    background: filterLength > 0 ? "#f16253" : "",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: columnWidth,
                    padding: '0',
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer"
                  }}
                >
                  <div>{params.colDef.headerName}</div>
                  <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                    <Box
                      onClick={(event) => handleOpenFilter(event, getLabel(col.value))}
                      sx={{ ml: 3, mt: 0.5 }}
                    >
                      <FilterListIcon sx={{ color: filters?.section?.length ? 'white' : '', fontSize: 18, cursor: "pointer" }} />
                    </Box>
                  </div>
                </div>

              )
            } else {
              return (
                <div style={{ fontWeight: 'bold', width: '100%', textAlign: 'center' }}>
                  {params.colDef.headerName}
                </div>
              )
            }
          },
          renderCell: (params) => {
            const record = params.row;

            if (col.value === 'part_description') {
              return (
                <Tooltip title={record['description']} placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'white',
                        color: 'black',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                        maxWidth: 300,
                        fontSize: '14px',
                        p: 1
                      }
                    }
                  }}>
                  <div style={{ whiteSpace: 'normal', cursor: 'pointer', wordBreak: 'break-word', overflow: 'visible' }}>
                    {record['description'] || record?.part_description}
                  </div>
                </Tooltip>
              );
            } else if (col.value === 'laydown') {
              return <div>{record['laydown_name']}</div>;
            } else if (col.value === 'part_number') {
              return <div>{record['part_name']}</div>;
            } else if (col.value === 'total_quantity') {
              return <div>{record['project_quantity']}</div>;
            } else if (col.value === 'time_stamp') {
              return formatTimestamp(record["created_at"]);
            } else if (col.value === 'checkpoint') {
              return <div>{record['checkpoint_name']}</div>;
            } else if (col.value === 'block') {
              return <div>{record['block_name'] || record['block']}</div>;
            } else if (col.value === 'addl_qty_incl_o&m_spares') {
              return <div>{record['additional_count']}</div>;
            } else if (col.value === 'project_quantity') {
              return <div style={{ textAlign: 'center' }}>{selectPart.length ? record['project_quantity_calc'] : record['default_project_quantity']}</div>;
            } else if (col.value === 'construction_spares') {
              return <div style={{ textAlign: 'center' }}>{record['spare_count']}</div>;
            } else if (col.value === 'on-site_qty') {
              return (
                <div>
                  {record['received_quantity'] ? (
                    <div style={{ textAlign: 'center'}}>
                      {record['received_quantity']}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', }}>{"-"}</div>
                  )}
                </div>
              );
            } else if (col.value === 'balance_qty') {
              return <div style={{ textAlign: 'center' }}>{record['project_quantity'] - record['received_quantity']}</div>;
            } else if (col.value === "acceptance_crieteria") {
              return (
                <Tooltip
                  title={record.acceptance_crieteria || ""}
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "white",
                        color: "black",
                        boxShadow: 3,
                        maxWidth: 300,
                        fontSize: 14,
                        p: 1,
                      }
                    }
                  }}
                >
                  <div style={{ whiteSpace: "normal", cursor: "pointer", wordBreak: "break-word", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {record.acceptance_crieteria}
                  </div>
                </Tooltip>
              );
            } else if (col.value === "status") {
              return (
                <Tooltip
                  title={record.punchlist_status === 0 ? "Open" : record.punchlist_status === 1 ? "Ready for Inspection" : "Resolved"}
                  placement="left"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "white",
                        color: "black",
                        boxShadow: 3,
                        maxWidth: 300,
                        fontSize: 14,
                        p: 1,
                      }
                    }
                  }}
                >
                  <div>
                    {record.punchlist_status === 0 ? "Open" : record.punchlist_status === 1 ? "Ready for Inspection" : "Resolved"}
                  </div>
                </Tooltip>
              )
            }
            else if (col.value === 'distributed_quantity') {
              return <div style={{ textAlign: 'center' }}>{record.distributed_quantity || 0}</div>;
            } else if (col.value === 'installed_quantity') {
              return <div style={{ textAlign: 'center' }}>{record.installed_quantity || 0}</div>;
            } else if (col.value === 'quantity_yet_to_receive') {
              return <div style={{ textAlign: 'center' }}>{record.quantity_yet_to_receive || 0}</div>;
            }
            // NEW: ITC/ITR cell renderers
            else if (col.value === "punchlist_category") {
              return <div>{startCase(record.punchlist_category)}</div>;
            } else if (col.value === "scope") {
              return (
                <div style={{ whiteSpace: 'normal', lineHeight: '1', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ marginTop: '8px', marginBottom: '2px' }}>{startCase(record.scope)}</div>
                  {record.label && <div style={{ display: 'flex', alignItems: 'center', marginLeft: '12px', fontSize: '0.875rem' }}>
                    <SubdirectoryArrowRightIcon sx={{ fontSize: 14, marginRight: '4px' }} />
                    <span style={{ paddingTop: "5px" }}>{record.label}</span>
                  </div>}
                </div>
              );
            } else if (col.value === "itr_reference_number") {
              return <div>{record.itr_reference_number}</div>;
            } else if (col.value === "part_name") {
              return <div>{record?.part_number?record?.part_number:record?.part_name}</div>;
            } else if (col.value === TERMS.SECTION_HEADER) {
              return <div>{record.section}</div>;
            } else if (col.value === TERMS.ROW_HEADER) {
              return <div>{record.row}</div>;
            } else if (col.value === "photos") {
              return (
                <Box display={"flex"} alignItems={"center"} gap={2} flexWrap={"wrap"}>
                  {record?.document?.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`preview-${idx}`}
                      style={{ width: 40, height: 40, objectFit: 'cover', cursor: 'pointer', borderRadius: 4, marginTop: 6 }}
                      onClick={() => handleImageClick(url)}
                    />
                  ))}
                </Box>
              )
            } else if (col.value === "action") {
              return (
                <Box>
                  <IconButton onClick={() => handleDelete(record)} size="small">
                    <DeleteIcon fontSize="small" sx={{ color: 'red' }} />
                  </IconButton>
                </Box>
              )

            } else {
              return <div>{params.value}</div>;
            }
          },
        };
      }
    });

  const columnGroups = {};

  visibleColumns.forEach(col => {

    let groupId = null;

    if (col.field && col.field.includes('.')) {
      groupId = col.field.split('.')[0];
    } else if (col.headerClassName && col.headerClassName.includes('-header')) {
      groupId = col.headerClassName.replace('-header', '');
    }

    if (groupId) {
      if (!columnGroups[groupId]) {
        columnGroups[groupId] = [];
      }
      columnGroups[groupId].push(col);
    }
  });

  Object.entries(columnGroups).forEach(([groupId, columns]) => {
    if (columns.length > 0) {
      columnGroupingModel.push({
        groupId: groupId,
        headerName: groupId.charAt(0).toUpperCase() + groupId.slice(1),
        headerClassName: 'header-group',
        children: columns.map(col => ({ field: col.field })),
        headerAlign: 'center',
      });
    }
  });

  visibleColumns.forEach(col => {
    if (col.children && col.children.length > 0) {
      columnGroupingModel.push({
        groupId: col.field || `group-${Math.random().toString(36).substr(2, 9)}`,
        headerName: col.headerName || col.field,
        headerClassName: col.headerClassName || 'header-group',
        children: col.children.map(child => ({ field: child.field })),
        headerAlign: col.headerAlign || 'center',
      });
    }
  });

  return { visibleColumns, columnGroupingModel };
};

export const getColumnsList = (data, checkedList, onChange) => (
  <Box display="flex" flexDirection="column">
    {data?.map((item, index) => (
      <Box key={index} display="flex" alignItems="center">
        <Checkbox
          checked={checkedList?.includes(item.key)}
          onChange={() => onChange(item.key)}
          size="small"
        />
        <Typography
          variant="span"
          component="span"
          fontSize={16}
          fontWeight={400}
          sx={{
            maxWidth: '180px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block'
          }}
        >
          {startCase(item.value)}
        </Typography>
      </Box>
    ))}
  </Box>
);


export const prepareBomColumnsAndGroups = (
  columns,
  partNameFilterOptions,
  isEdit,
  list,
  handleChange
) => {
  const columnGroupingModel = [];

  const visibleColumns = columns
    .filter(col => !col.hidden)
    .flatMap((col) => {
      const getWidth = (value) => {
        if (value === "part_description") return 650;
        if (value === "part_number") return 100;
        return 350;
      };

      if (col.children?.length) {
        return col.children.map((child, index) => {
          const field = `${col.value}.${child}`;
          const width = getWidth(field);
          const baseProps = {
            field,
            width,
            minWidth: width,
            headerName: child === "percentage" ? "%" : startCase(child),
            headerClassName: `${col.value}-header`,
            sortable: false,
            filterable: true,
            align: "center",
            flex: 1,
            renderCell: (params) => {
              const record = params.row;
              if (col.value === "distribution" || col.value === "installed") {
                const key = col.value;
                const nameKey = `${key}_name`;
                const selectValue = record?.[key] ? record?.[key] : "none";

                return isEdit ? (
                  <Select
                    value={selectValue}
                    onChange={(e) => handleChange?.(e.target.value, record, key)}
                    fullWidth
                    variant="standard"
                    sx={{
                      fontSize: 16,
                      '&:before': { borderBottom: 'none' },
                      '&:after': { borderBottom: 'none' },
                      '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                      '&.Mui-focused:before': { borderBottom: 'none' },
                      '&.Mui-focused:after': { borderBottom: 'none' }
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                          overflow: 'auto'
                        }
                      }
                    }}
                  >
                    <MenuItem value="none" sx={{ fontWeight: 500, fontSize: "16px" }}>None Selected</MenuItem>
                    <MenuItem value="all" sx={{ fontWeight: 500, fontSize: "16px" }}>Overall</MenuItem>
                    {list.length && list?.flatMap((group) => [
                      <MenuItem key={`parent-${group?.id}`} value={`parent-${group?.id}`}>
                        <Box fontWeight={600}>{startCase(group?.label)}</Box>
                      </MenuItem>,
                      ...(group?.children?.map((child) => (
                        <MenuItem
                          key={`child-${child?.id}`}
                          value={child?.id}
                          sx={{ pl: 3, fontSize: "16px" }}

                        >
                          {startCase(child?.label)}
                        </MenuItem>
                      )) || [])
                    ])}
                  </Select>
                ) : (
                  <div className="flex items-center justify-between px-4">
                    <div>{record?.[nameKey]}</div>
                  </div>
                );
              } else {
                const value = record[`${col.value}_${child}`];
                return <div>{value}</div>;
              }
            }
          };
          return baseProps;
        });
      }

      const width = getWidth(col.value);
      return {
        field: col.value,
        width,
        minWidth: width,
        headerName: startCase(col.value),
        sortable: false,
        filterable: true,
        flex: 1,
        renderCell: (params) => {
          const record = params.row;

          switch (col.value) {
            case "part_description":
              return (
                <Tooltip
                  title={record.description}
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "white",
                        color: "black",
                        boxShadow: 3,
                        maxWidth: 300,
                        fontSize: 14,
                        p: 1,
                      }
                    }
                  }}
                >
                  <div style={{ whiteSpace: "normal", cursor: "pointer", wordBreak: "break-word" }}>
                    {record.description || record.part_description}
                  </div>
                </Tooltip>
              );
            case "project_quantity":
              return <div>{record.default_project_quantity}</div>;
            case "balance_qty":
              return <div>{record.project_quantity - record.received_quantity}</div>;
            case "part_number":
              return <div style={{ fontWeight: 600 }}>{record.part_name}</div>;
            default:
              return <div>{params.value}</div>;
          }
        }
      };
    });

  const columnGroupsMap = {};

  visibleColumns.forEach((col) => {
    const groupId = col.field?.split(".")[0];
    if (groupId && groupId !== col.field) {
      if (!columnGroupsMap[groupId]) columnGroupsMap[groupId] = [];
      columnGroupsMap[groupId].push(col);
    }
  });

  Object.entries(columnGroupsMap).forEach(([groupId, children]) => {
    columnGroupingModel.push({
      groupId,
      headerName: startCase(groupId),
      headerClassName: "header-group",
      children: children.map(col => ({ field: col.field })),
      headerAlign: "center",
    });
  });

  return { visibleColumns, columnGroupingModel };
};


export const appendPath = (svgRef, x, y) => {
  d3.select(svgRef?.current)
    .append("path")
    .attr("d", "M 6 -5 L 15 0 L 15 9 L 6 15 L -3 9 L -3 0 Z")
    .attr("transform", `translate(${x - 6}, ${y - 6})`)
    .attr("stroke", "black")
    .attr("stroke-width", "1px")
    .attr("fill", "white");
};

export const appendText = (svgRef, x, y, text) => {
  d3.select(svgRef?.current)
    .append("text")
    .attr("fill", "black")
    .attr("x", x)
    .attr("y", y)
    .attr("text-anchor", "middle")
    .style("font-size", "10px")
    .style("font-weight", "600")
    .text(text);
};


export const SectionList = (data) => {

  const temp = new Set();

  if (data) {
    Object.values(data)
      .flatMap(piers => Object.values(piers))
      .forEach(pierData => {
        if (pierData?.section) {
          temp.add(pierData.section);
        }
      });
  }
  return Array.from(temp);
}

export const SectionListCount = (data) => {
  const result = {};

  if (!data) return result;

  const sections = new Set();

  const sectionTrackerRows = {};

  Object.entries(data).forEach(([trackerRowId, pierArray]) => {
    pierArray.forEach(pier => {
      if (pier?.section) {
        sections.add(pier.section);

        if (!sectionTrackerRows[pier.section]) {
          sectionTrackerRows[pier.section] = new Set();
        }

        sectionTrackerRows[pier.section].add(trackerRowId);
      }
    });
  });

  Array.from(sections).forEach(section => {
    const sectionKey = `sections_${section}`;
    result[sectionKey] = {
      selectedCount: sectionTrackerRows[section]?.size || 0
    };
  });

  return result;
}

export const filterBySection = (data, filterValue, type = "section", prevState) => {
  if (!data) return {};

  const result = {
    sections: {},
    trackerTypes: {},
    count: 0
  };

  const trackerTypeCounts = {};

  if (type === "section" && filterValue) {
    const sections = Array.isArray(filterValue) ? filterValue : [filterValue];

    sections.forEach(section => {
      const sectionKey = `sections_${section}`;
      result.sections[sectionKey] = {
        selectedRows: {},
        selectedCount: 0,
        totalCount: 0
      };

      if (data.piers) {
        Object.entries(data.piers).forEach(([trackerRowId, pierArray]) => {
          const filteredPiers = pierArray.filter(pier => pier.section === section);

          if (filteredPiers.length > 0) {
            const trackerTypeId = filteredPiers[0].tracker_type_id;
            const trackerTypeKey = `trackerType_${trackerTypeId}`;

            if (!result.trackerTypes[trackerTypeKey]) {
              result.trackerTypes[trackerTypeKey] = {
                selectedRows: {},
                selectedCount: 0,
                totalCount: 0
              };
              trackerTypeCounts[trackerTypeKey] = 0;
            }

            result.sections[sectionKey].totalCount++;
            result.sections[sectionKey].selectedRows[trackerRowId] = {
              piers: filteredPiers,
              tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
            };
            result.sections[sectionKey].selectedCount++;

            result.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
              piers: filteredPiers,
              tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
            };
            result.trackerTypes[trackerTypeKey].selectedCount++;
            trackerTypeCounts[trackerTypeKey]++;

            result.count++;
          }
        });
      }
    });
  } else if (type === "tracker" && filterValue) {
    const trackerTypeIds = Array.isArray(filterValue) ? filterValue : [filterValue];

    if (data.piers) {
      Object.entries(data.piers).forEach(([trackerRowId, pierArray]) => {
        if (pierArray.length > 0) {
          const trackerTypeId = pierArray[0].tracker_type_id;

          if (trackerTypeIds.includes(trackerTypeId)) {
            const section = pierArray[0].section;
            const sectionKey = `sections_${section}`;
            const trackerTypeKey = `trackerType_${trackerTypeId}`;

            if (!result.sections[sectionKey]) {
              result.sections[sectionKey] = {
                selectedRows: {},
                selectedCount: 0,
                totalCount: 0
              };
            }

            if (!result.trackerTypes[trackerTypeKey]) {
              result.trackerTypes[trackerTypeKey] = {
                selectedRows: {},
                selectedCount: 0,
                totalCount: 0
              };
              trackerTypeCounts[trackerTypeKey] = 0;
            }

            result.sections[sectionKey].totalCount++;
            result.sections[sectionKey].selectedRows[trackerRowId] = {
              piers: pierArray,
              tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
            };
            result.sections[sectionKey].selectedCount++;

            result.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
              piers: pierArray,
              tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
            };
            result.trackerTypes[trackerTypeKey].selectedCount++;
            trackerTypeCounts[trackerTypeKey]++;

            result.count++;
          }
        }
      });
    }
  } else if (type === "individualTracker" && filterValue) {

    const result = { ...prevState };

    const trackerData = filterValue;
    const trackerRowId = trackerData.tracker_row_id.toString();
    const trackerTypeId = trackerData.tracker_type_id;
    const section = trackerData.section;

    if (data.piers && data.piers[trackerRowId]) {
      const pierArray = data.piers[trackerRowId];
      const sectionKey = `sections_${section}`;
      const trackerTypeKey = `trackerType_${trackerTypeId}`;

      if (!result.sections) result.sections = {};
      if (!result.sections[sectionKey]) {
        result.sections[sectionKey] = {
          selectedRows: {},
          selectedCount: 0,
          totalCount: 0
        };
      }

      if (!result.trackerTypes) result.trackerTypes = {};
      if (!result.trackerTypes[trackerTypeKey]) {
        result.trackerTypes[trackerTypeKey] = {
          selectedRows: {},
          selectedCount: 0,
          totalCount: 0
        };
      }

      if (!result.count) result.count = 0;

      result.sections[sectionKey].selectedRows[trackerRowId] = {
        piers: pierArray,
        tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
      };
      result.sections[sectionKey].selectedCount = Object.keys(result.sections[sectionKey].selectedRows).length;

      result.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
        piers: pierArray,
        tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
      };
      result.trackerTypes[trackerTypeKey].selectedCount = Object.keys(result.trackerTypes[trackerTypeKey].selectedRows).length;

      result.count = Object.values(result.sections).reduce((acc, section) => acc + Object.keys(section.selectedRows).length, 0);
    }

    return result;
  }
  else if (type === "all") {
    if (data.piers) {
      Object.entries(data.piers).forEach(([trackerRowId, pierArray]) => {
        if (pierArray.length > 0) {
          const section = pierArray[0].section;
          const trackerTypeId = pierArray[0].tracker_type_id;
          const sectionKey = `sections_${section}`;
          const trackerTypeKey = `trackerType_${trackerTypeId}`;

          if (!result.sections[sectionKey]) {
            result.sections[sectionKey] = {
              selectedRows: {},
              selectedCount: 0,
              totalCount: 0
            };
          }

          if (!result.trackerTypes[trackerTypeKey]) {
            result.trackerTypes[trackerTypeKey] = {
              selectedRows: {},
              selectedCount: 0,
              totalCount: 0
            };
            trackerTypeCounts[trackerTypeKey] = 0;
          }

          result.sections[sectionKey].totalCount++;
          result.sections[sectionKey].selectedRows[trackerRowId] = {
            piers: pierArray,
            tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
          };
          result.sections[sectionKey].selectedCount++;

          result.trackerTypes[trackerTypeKey].selectedRows[trackerRowId] = {
            piers: pierArray,
            tt: data.tt && data.tt[trackerRowId] ? data.tt[trackerRowId] : []
          };
          result.trackerTypes[trackerTypeKey].selectedCount++;
          trackerTypeCounts[trackerTypeKey]++;

          result.count++;
        }
      });
    }
  }

  Object.keys(result.trackerTypes).forEach(trackerTypeKey => {
    result.trackerTypes[trackerTypeKey].totalCount = trackerTypeCounts[trackerTypeKey];
  });

  return result;
}

export const getSelectedRows = (data) => {
  const selectedRowIds = [];

  Object.values(data).forEach(section => {
    if (section.selectedRows) {
      const rowIds = Object.keys(section.selectedRows).map(rowId => Number(rowId));
      selectedRowIds.push(...rowIds);
    }
  });

  return selectedRowIds;
};


export function convertSelectedRowsToOldFormat(data, sourceType = "sections") {
  const result = [];

  if (sourceType === "sections" && data.sections) {
    for (const sectionKey in data.sections) {
      const section = data.sections[sectionKey];

      if (section.selectedRows) {
        for (const trackerRowId in section.selectedRows) {
          const rowData = section.selectedRows[trackerRowId];

          if (rowData.piers && rowData.piers.length > 0) {
            const pier = rowData.piers[0];
            const trackerTypeId = pier.tracker_type_id;
            const row = pier.row;
            const sectionNumber = pier.section;

            const formattedString = `trow-${trackerRowId}-${trackerTypeId}-${row}-${sectionNumber}`;
            result.push(formattedString);
          }
        }
      }
    }
  }
  else if (sourceType === "trackerTypes" && data.trackerTypes) {
    for (const trackerTypeKey in data.trackerTypes) {
      const trackerType = data.trackerTypes[trackerTypeKey];

      if (trackerType.selectedRows) {
        for (const trackerRowId in trackerType.selectedRows) {
          const rowData = trackerType.selectedRows[trackerRowId];

          if (rowData.piers && rowData.piers.length > 0) {
            const pier = rowData.piers[0];
            const trackerTypeId = pier.tracker_type_id;
            const row = pier.row;
            const sectionNumber = pier.section;

            const formattedString = `trow-${trackerRowId}-${trackerTypeId}-${row}-${sectionNumber}`;
            result.push(formattedString);
          }
        }
      }
    }
  }

  return result;
}


export const findPierAndTtByTrackerTypeId = (data = {}, trackerTypeId) => {
  const { piers = {}, tt = {} } = data;
  const result = { pier: {}, tt: {} };

  for (const [, items] of Object.entries(piers)) {
    if (items?.some(item => item?.pier_part_tracker_type_id === trackerTypeId)) {
      result.pier = items;
      break;
    }
  }

  for (const [, items] of Object.entries(tt)) {
    if (items?.some(item => item?.tracker_type_id === trackerTypeId)) {
      result.tt = items;
      break;
    }
  }


  return result;
};

export const findPierAndTtByTrackerRowId = (data = {}, trackerRowId) => {
  const { piers = {}, tt = {} } = data;
  const result = { pier: [], tt: [] };

  result.pier = piers[trackerRowId] || [];
  result.tt = tt[trackerRowId] || [];

  return result;
};

export const getView = (value) => {
  if (value === 0) return "piers";
  else if (value === 1) return "structures";
  else return "module";
};

export const formatUnixDate = (unixTimestamp) => {
  return formatUnixDateUtil(unixTimestamp);
};

export const getValue = (values) => {
  if (values === 0) return "Open";
  if (values === 1) return "Ready for Inspection";
  if (values === 2) return "Resolved";
  return "Deleted";
}

export const convertFromTimestamp = (timestamp) => {
  return convertFromTimestampUtil(timestamp);
};

export const DownloadExcelFile = (headers, data, currentProject, setSnackbar, title, alies) => {

  const sheetData = [];

  const groupRow = [];
  const subHeaderRow = [];
  const merges = [];

  let colIndex = 0;

  headers.forEach(header => {
    if (header.hidden) return;

    const span = header.children?.length || 1;
    const groupLabel = startCase(header.value);

    groupRow.push(groupLabel);
    if (span > 1) {
      merges.push({
        s: { r: 6, c: colIndex },
        e: { r: 6, c: colIndex + span - 1 },
      });
      for (let i = 1; i < span; i++) {
        groupRow.push(null);
      }
    }

    if (header.children?.length) {
      header.children.forEach(child => {
        subHeaderRow.push(startCase(child));
      });
    } else {
      subHeaderRow.push('');
    }

    colIndex += span;
  });

  const headerInfo = [
    ['Project Number', currentProject?.project_number?.toString() || ''],
    ['Project Name', currentProject?.name || ''],
    ['Report', 'CP Summary'],
    ['Report Date', new Date().toLocaleDateString()],
    [''], [''],
  ];

  sheetData.push(...headerInfo, groupRow);

  const hasChildren = headers.some(h => Array.isArray(h.children) && h.children.length > 0);
  if (hasChildren) {
    sheetData.push(subHeaderRow);
  }

  data.forEach(block => {
    const row = [];
    headers.forEach(header => {
      if (header.hidden) return;

      if (header.children?.length) {
        const section = block?.[header.value];
        header.children.forEach(child => {
          if (child === "counts") {
            const deployed = section.deployed_count ?? 0;
            const total = section.total_count ?? 0;
            row.push(`${deployed} of ${total}`);
          } else if (child === "progress") {
            const value = Math.floor(section?.[child] ?? 0);
            row.push(`${value}%`);
          } else if (child === "percentage") {
            if (header.value === "distributed") {
              const value = block?.[`distribution_progress`]
              row.push(`${Math.floor(value)}%`);
            } else {
              const value = block?.[`${header.value}_progress`]
              row.push(`${Math.floor(value)}%`);
            }
          } else if (child === "qty") {
            const value = block?.[`${header.value}_quantity`]
            row.push(`${value}`);
          }
        });
      } else {
        switch (header.value) {
          case 'blocks':
          case 'block':
            row.push(block.block_name || block?.block || '');
            break;
          case 'time_stamp':
            const timeData = formatUnixDate(block.created_at);
            row.push(timeData?.date || '');
            break;
          case 'row':
            row.push(block.row || '');
            break;
          case 'scope':
            row.push(block.scope || '');
            break;
          case 'checkpoint':
            row.push(block.checkpoint_name || '');
            break;
          case 'acceptance_crieteria':
            row.push(block.acceptance_crieteria || '');
            break;
          case 'comments':
            row.push(block.comments || '-');
            break;
          case 'status':
            row.push(getValue(block.punchlist_status) || '');
            break;
          case 'photos':
            row.push('photos');
            break;
          case 'part_name':
            row.push(block.part_name || '');
            break;
          case 'part_description':
            row.push(block.description || block.part_description || '');
            break;
        case 'laydown':
          row.push(block.laydown_name || '');
          break;
          case 'project_quantity':
            row.push(block.default_project_quantity || 0);
            break;
          case 'total_quantity':
            row.push(block.project_quantity || 0);
            break;
          case 'construction_spares':
            row.push(block.spare_count || 0);
            break;
          case 'on-site_qty':
            row.push(block.received_quantity || 0);
            break;
          case 'balance_qty':
            row.push(block.project_quantity - block.received_quantity || 0);
            break;
          case 'addl_qty_incl_o&m_spares':
            row.push(block.additional_count || 0);
            break;
          case 'section':
            row.push(block.section || 0);
            break;
          case 'name':
            row.push(block.name || 0);
            break;
          case `${alies}`:
            row.push(block.name || 0);
            break;

          case 'delivery date':
            const deliveryDateData = formatUnixDate(block.date_time);
            row.push(deliveryDateData?.date || '');
            break;

          case 'delivery time':
            const deliveryTimeData = formatUnixDate(block.date_time);
            row.push(deliveryTimeData?.time || '');
            break;
          default:
            row.push('');
        }
      }
    });
    sheetData.push(row);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  worksheet['!merges'] = merges;
  worksheet['!cols'] = Array(subHeaderRow.length || groupRow.length).fill({ wch: 18 });

  for (let r = 0; r < 4; r++) {
    const cell = XLSX.utils.encode_cell({ r, c: 0 });
    if (worksheet[cell]) {
      worksheet[cell].s = {
        font: { bold: true },
        alignment: { horizontal: 'left' },
      };
    }
  }

  const borderStyle = {
    top: { style: 'thin', color: { rgb: '000000' } },
    bottom: { style: 'thin', color: { rgb: '000000' } },
    left: { style: 'thin', color: { rgb: '000000' } },
    right: { style: 'thin', color: { rgb: '000000' } },
  };

  const totalCols = subHeaderRow.length || groupRow.length;
  const groupRowIndex = 6;
  const subHeaderRowIndex = hasChildren ? 7 : null;

  for (let c = 0; c < totalCols; c++) {
    const groupCell = XLSX.utils.encode_cell({ r: groupRowIndex, c });
    if (!worksheet[groupCell]) worksheet[groupCell] = { v: '', t: 's' };
    worksheet[groupCell].s = {
      alignment: { horizontal: 'center', vertical: 'center' },
      font: { bold: true },
      border: borderStyle,
    };

    if (subHeaderRowIndex !== null) {
      const subCell = XLSX.utils.encode_cell({ r: subHeaderRowIndex, c });
      if (!worksheet[subCell]) worksheet[subCell] = { v: '', t: 's' };
      worksheet[subCell].s = {
        alignment: { horizontal: 'center' },
        font: { bold: true },
        border: borderStyle,
      };
    }
  }

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];
      if (cell && cell.v !== undefined && cell.v !== null && cell.v !== '') {
        const existingStyle = cell.s || {};
        cell.s = {
          ...existingStyle,
          border: borderStyle,
        };
      }
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, title);
  XLSX.writeFile(workbook, `${title} ${currentProject?.name}.xlsx`);
};

export const transformTrackerTypes = (rows = []) => {
  return rows.map((item) => {
    const tracker_types = Array.isArray(item.tracker_types) ? item.tracker_types : [];

    const trackerTypeMap = tracker_types.reduce((acc, tracker) => {
      acc[tracker.name] = tracker;
      return acc;
    }, {});

    const { tracker_types: _, ...rest } = item;
    return {
      ...rest,
      ...trackerTypeMap,
    };
  });
};

export const mergeHeadersWithChecklist = (headers = [], checklist = []) => {
  const checklistHeaders = Array.isArray(checklist)
    ? checklist.map((item) => ({
        key: item.id,
        value: item.tracker_type,
        hidden: false,
      }))
    : [];

  return [...headers, ...checklistHeaders];
};

export function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, (state) => state.pagination.paginationModel.page);
  const pageSize = useGridSelector(apiRef, (state) => state.pagination.paginationModel.pageSize);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const rowCount = useGridSelector(apiRef, (state) => state.pagination.rowCount);

  const [pageSizeInput, setPageSizeInput] = useState(pageSize ? pageSize.toString() : '10');

  useEffect(() => {
    setPageSizeInput(pageSize ? pageSize.toString() : '10');
  }, [pageSize]);

  const handlePageSizeChange = (event) => {
    const value = event.target.value;
    setPageSizeInput(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      apiRef.current.setPageSize(numValue);
    }
  };

  const handlePageSizeBlur = () => {
    const numValue = parseInt(pageSizeInput, 10);
    if (isNaN(numValue) || numValue <= 0) {
      setPageSizeInput(pageSize.toString());
    }
  };

  const handlePageChange = (event, newPage) => {
    apiRef.current.setPage(newPage - 1);
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, rowCount);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 16px',
        borderTop: '1px solid rgba(224, 224, 224, 1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
        <Typography variant="body2">
          Rows per page:
        </Typography>
        <TextField
          size="small"
          value={pageSizeInput}
          onChange={handlePageSizeChange}
          onBlur={handlePageSizeBlur}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handlePageSizeBlur();
            }
          }}
          type="number"
          inputProps={{
            min: 1,
            style: { textAlign: 'center' }
          }}
          sx={{
            width: '70px',
            '& .MuiOutlinedInput-root': {
              height: '32px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '6px 8px',
            }
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2">
          {`${startRow}-${endRow} of ${rowCount}`}
        </Typography>
        <Pagination
          count={pageCount}
          page={page + 1}
          onChange={handlePageChange}
          shape="rounded"
          size="small"
          showFirstButton
          showLastButton
          siblingCount={0}
          boundaryCount={1}
          sx={{
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap',
            },
            '& .MuiPaginationItem-root': {
              minWidth: '32px',
              height: '32px',
            }
          }}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={{
                previous: () => <span>‹</span>,
                next: () => <span>›</span>,
                first: () => <span>«</span>,
                last: () => <span>»</span>,
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
}

export const sortLaydownsByNameAsc = (laydowns) => {
  return [...laydowns].sort((a, b) => a.name.localeCompare(b.name));
};

